import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const STEPS = [
  'Business Details',
  'Category & Profile',
  'KYC & Documents',
  'Bank Details',
];

const CATEGORY_OPTIONS = [
  { value: 'Handicrafts',     label: '🎨 Handicrafts & Art' },
  { value: 'Sweets/SevaOffering',   label: '🍡 Sweets & SevaOffering' },
  { value: 'Books',           label: '📚 Religious Books & Scriptures' },
  { value: 'Ceremony Items',      label: '🪔 Ceremony Items & Accessories' },
  { value: 'Sacred Herbs',    label: '🌿 Sacred Herbs & Incense' },
  { value: 'Clothing',        label: '👘 Religious Clothing & Garments' },
  { value: 'Idols & Statues', label: '🕉️ Idols & Statues' },
  { value: 'Oils & Ghee',     label: '🪔 Oils, Ghee & Diyas' },
  { value: 'Gems & Malas',    label: '📿 Gems, Malas & Yantras' },
  { value: 'Other',           label: '📦 Other Products' },
];

const FOOD_CATEGORIES = ['Sweets/SevaOffering', 'Oils & Ghee'];

const VendorRegister = () => {
  const navigate = useNavigate();
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [docModal, setDocModal] = useState(null); // { label, url }
  const [toast, setToast]   = useState(null);

  // Document upload states
  const [uploading, setUploading] = useState({});

  const [formData, setFormData] = useState({
    // Step 1: Business Details
    legalEntityName: '', shopName: '', businessType: '',
    contactPerson: '', email: '', phone: '',
    // Step 2: Category & Profile
    businessCategories: [], businessDescription: '',
    // Step 3: KYC & Documents
    gstin: '', panNumber: '', fssaiLicense: '',
    registeredAddress: '', warehouseAddress: '',
    documents: { gstinCert: '', panCard: '', fssaiCert: '' },
    // Step 4: Bank Details
    bank: {
      bankName: '', accountNumber: '', ifscCode: '',
      accountHolder: '', cancelledCheck: '',
    },
  });

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBankChange = (e) => {
    setFormData(prev => ({
      ...prev,
      bank: { ...prev.bank, [e.target.name]: e.target.value },
    }));
  };

  const handleCategoryToggle = (val) => {
    setFormData(prev => ({
      ...prev,
      businessCategories: prev.businessCategories.includes(val)
        ? prev.businessCategories.filter(c => c !== val)
        : [...prev.businessCategories, val],
    }));
  };

  const isFoodVendor = formData.businessCategories.some(c => FOOD_CATEGORIES.includes(c));

  // Document upload handler
  const handleDocumentUpload = async (field, file, nested = false) => {
    if (!file) return;
    const key = nested ? `doc_${field}` : field;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('document', file);
      const res = await api.post('/upload/document?folder=vendor-documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        if (nested) {
          setFormData(prev => ({
            ...prev,
            documents: { ...prev.documents, [field]: res.data.data.url },
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            bank: { ...prev.bank, [field]: res.data.data.url },
          }));
        }
        showToast('success', '✅ Document uploaded successfully.');
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Document upload failed.');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleNext = (e) => { e.preventDefault(); setStep(s => s + 1); };
  const handleBack = () => setStep(s => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/register-vendor', {
        legalEntityName:     formData.legalEntityName,
        shopName:            formData.shopName,
        businessType:        formData.businessType,
        contactPerson:       formData.contactPerson,
        email:               formData.email,
        phone:               formData.phone,
        businessCategories:  formData.businessCategories,
        businessDescription: formData.businessDescription,
        gstin:               formData.gstin,
        panNumber:           formData.panNumber,
        fssaiLicense:        formData.fssaiLicense,
        registeredAddress:   formData.registeredAddress,
        warehouseAddress:    formData.warehouseAddress,
        documents:           formData.documents,
        bank:                formData.bank,
      });
      if (res.data.success) {
        showToast('success', '🙏 Application submitted! A Super Admin will review your KYC documents and activate your Vendor Dashboard within 2–5 business days.');
        setTimeout(() => navigate('/login'), 4000);
      } else {
        showToast('error', res.data.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Shared styles (same pattern as CentreRegister) ─────────────────────────
  const inp = {
    width: '100%', padding: '0.75rem',
    background: '#1a1a1a', border: '1px solid #333',
    borderRadius: '0.5rem', color: '#fff',
    boxSizing: 'border-box', fontSize: '0.875rem',
  };
  const lbl = { display: 'block', marginBottom: '0.4rem', color: '#E8B84B', fontSize: '0.8rem', fontWeight: 600 };
  const row = { display: 'flex', gap: '1rem' };
  const col = { flex: 1 };
  const btnBack = { flex: 1, padding: '0.75rem', background: 'transparent', border: '1px solid #444', borderRadius: '0.5rem', color: '#888', cursor: 'pointer', fontWeight: 600 };
  const btnNext = { flex: 2, padding: '0.75rem', background: 'linear-gradient(90deg, #E8B84B, #B8923A)', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 800, cursor: 'pointer' };

  // ── Helpers for document viewer ─────────────────────────────────────────────
  // Detect PDFs: check .pdf extension OR /raw/upload/ path (new Cloudinary uploads)
  // OR if it's stored in vendor-documents folder (all are KYC docs = PDFs or images)
  const isPdf = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.pdf') || lower.includes('/raw/upload/');
  };

  // ── Document upload button component ───────────────────────────────────────
  const DocUpload = ({ label, field, value, nested = true, accept = 'image/*,application/pdf', hint = '' }) => {
    const fileRef = useRef();
    const key = nested ? `doc_${field}` : field;
    const isUploading = uploading[key];
    const fileUrl = nested ? formData.documents[field] : formData.bank[field];
    const isDone = !!fileUrl;

    return (
      <div>
        <label style={lbl}>{label}</label>
        {hint && <p style={{ color: '#555', fontSize: '0.72rem', marginBottom: '0.4rem', marginTop: 0 }}>{hint}</p>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={isUploading}
            style={{
              padding: '0.6rem 1.2rem',
              background: isDone ? 'rgba(34,197,94,0.1)' : 'rgba(212,175,55,0.08)',
              border: `1px solid ${isDone ? 'rgba(34,197,94,0.4)' : 'rgba(212,175,55,0.3)'}`,
              borderRadius: '0.4rem',
              color: isDone ? '#4ade80' : '#E8B84B',
              cursor: isUploading ? 'wait' : 'pointer',
              fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            {isUploading ? '⏳ Uploading…' : isDone ? '✅ Uploaded' : '📎 Upload File'}
          </button>
          {isDone && (
            <button
              type="button"
              onClick={() => setDocModal({ label, url: fileUrl })}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: isPdf(fileUrl) ? '#a78bfa' : '#22c55e',
                fontSize: '0.72rem', fontWeight: 600, textDecoration: 'underline', padding: 0,
              }}
            >
              {isPdf(fileUrl) ? '📄 View PDF' : '🖼️ View Image'}
            </button>
          )}
          {!isDone && <span style={{ color: '#444', fontSize: '0.72rem' }}>JPG, PNG or PDF • Max 20MB</span>}
        </div>
        <input
          type="file"
          accept={accept}
          ref={fileRef}
          style={{ display: 'none' }}
          onChange={e => handleDocumentUpload(field, e.target.files[0], nested)}
        />
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 20% 50%, rgba(212,175,55,0.07) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(255,153,51,0.04) 0%, transparent 50%), #0a0a0a',
      padding: '2rem 1rem',
    }}>
      {/* ── Document Viewer Modal ─────────────────────────────────────────────── */}
      {docModal && (
        <div
          onClick={() => setDocModal(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '900px', background: '#111', borderRadius: '1rem', overflow: 'hidden', border: '1px solid rgba(212,175,55,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.85rem 1.25rem', borderBottom: '1px solid #1e1e1e' }}>
              <span style={{ color: '#E8B84B', fontWeight: 700, fontSize: '0.875rem' }}>
                {isPdf(docModal.url) ? '📄' : '🖼️'} {docModal.label}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <a href={docModal.url} target="_blank" rel="noreferrer"
                  style={{ padding: '0.35rem 0.85rem', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '0.4rem', color: '#E8B84B', fontSize: '0.75rem', fontWeight: 600, textDecoration: 'none' }}>
                  ↗ Open in Tab
                </a>
                <button onClick={() => setDocModal(null)}
                  style={{ padding: '0.35rem 0.85rem', background: 'rgba(248,68,68,0.1)', border: '1px solid rgba(248,68,68,0.25)', borderRadius: '0.4rem', color: '#f87171', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                  ✕ Close
                </button>
              </div>
            </div>
            <div style={{ background: '#0a0a0a', height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isPdf(docModal.url) ? (
                <object
                  data={docModal.url}
                  type="application/pdf"
                  style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff' }}
                >
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#fff' }}>
                    <p>Your browser does not support inline PDF viewing.</p>
                    <a href={docModal.url} target="_blank" rel="noreferrer" style={{ color: '#E8B84B', textDecoration: 'underline' }}>Click here to download the PDF</a>
                  </div>
                </object>
              ) : (
                <img src={docModal.url} alt={docModal.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '0.5rem' }} />
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ width: '100%', maxWidth: '620px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🏪</div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 900, letterSpacing: '0.1em', background: 'linear-gradient(135deg, #E8B84B, #FFD26E, #B8923A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.2rem' }}>Ananda Marga</h1>
          <p style={{ color: '#555', fontSize: '0.7rem', letterSpacing: '0.25em', textTransform: 'uppercase' }}>Vendor Onboarding Portal</p>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
          {STEPS.map((label, idx) => {
            const num = idx + 1;
            const done   = step > num;
            const active = step === num;
            return (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 900,
                    background: done ? '#E8B84B' : active ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${done || active ? '#E8B84B' : '#2a2a2a'}`,
                    color: done ? '#000' : active ? '#E8B84B' : '#444',
                    transition: 'all 0.3s',
                  }}>
                    {done ? '✓' : num}
                  </div>
                  <span style={{ fontSize: '0.55rem', color: active ? '#E8B84B' : done ? '#888' : '#444', marginTop: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: '2px', background: step > num ? '#E8B84B' : '#1e1e1e', marginTop: '13px', transition: 'background 0.3s' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ background: toast.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(248,68,68,0.1)', border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(248,68,68,0.3)'}`, borderRadius: '0.5rem', padding: '0.85rem 1rem', color: toast.type === 'success' ? '#4ade80' : '#f87171', marginBottom: '1rem', fontSize: '0.85rem' }}>
            {toast.text}
          </div>
        )}

        <div style={{ padding: '2rem', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(212,175,55,0.1)', borderRadius: '1rem', backdropFilter: 'blur(12px)' }}>

          {/* ─── STEP 1: Business Details ─── */}
          {step === 1 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Business Details</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Legal and contact information of your business.</p>
              </div>
              <div>
                <label style={lbl}>Legal Entity Name *</label>
                <input name="legalEntityName" value={formData.legalEntityName} onChange={handleChange} required placeholder="e.g. Sharma Handicrafts Private Limited" style={inp} />
              </div>
              <div>
                <label style={lbl}>Brand / Shop Name *</label>
                <input name="shopName" value={formData.shopName} onChange={handleChange} required placeholder="e.g. Ceremony Bhandar" style={inp} />
              </div>
              <div>
                <label style={lbl}>Business Type *</label>
                <select name="businessType" value={formData.businessType} onChange={handleChange} required style={{ ...inp, appearance: 'auto' }}>
                  <option value="">— Select business type —</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                  <option value="Partnership">Partnership</option>
                  <option value="LLP">LLP (Limited Liability Partnership)</option>
                  <option value="Private Limited">Private Limited Company</option>
                </select>
              </div>
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>Contact Person (Owner) *</label>
                  <input name="contactPerson" value={formData.contactPerson} onChange={handleChange} required placeholder="Full name" style={inp} />
                </div>
                <div style={col}>
                  <label style={lbl}>Phone Number *</label>
                  <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="+91 XXXXX XXXXX" style={inp} />
                </div>
              </div>
              <div>
                <label style={lbl}>Business Email * <span style={{ color: '#666', fontWeight: 400 }}>(Login credentials will be sent here after approval)</span></label>
                <input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="business@example.com" style={inp} />
              </div>
              <button type="submit" style={{ ...btnNext, flex: 'none', marginTop: '0.5rem' }}>Next: Category & Profile →</button>
            </form>
          )}

          {/* ─── STEP 2: Category & Profile ─── */}
          {step === 2 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Business Category & Profile</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>What do you sell? Select all that apply — this determines which centres and customers you're matched with.</p>
              </div>
              {formData.businessCategories.length === 0 && (
                <div style={{ padding: '0.65rem 1rem', background: 'rgba(248,68,68,0.06)', border: '1px solid rgba(248,68,68,0.15)', borderRadius: '0.4rem' }}>
                  <p style={{ color: '#f87171', fontSize: '0.75rem', margin: 0 }}>⚠️ Please select at least one category to continue.</p>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {CATEGORY_OPTIONS.map(opt => {
                  const selected = formData.businessCategories.includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      onClick={() => handleCategoryToggle(opt.value)}
                      style={{
                        padding: '0.85rem 1rem', borderRadius: '0.5rem', cursor: 'pointer',
                        border: `1px solid ${selected ? '#E8B84B' : '#2a2a2a'}`,
                        background: selected ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.02)',
                        color: selected ? '#FFD26E' : '#777',
                        fontSize: '0.875rem', fontWeight: selected ? 600 : 400,
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.75rem',
                      }}
                    >
                      <span style={{ fontSize: '1.1rem', opacity: selected ? 1 : 0.5 }}>{selected ? '✅' : '⬜'}</span>
                      {opt.label}
                    </div>
                  );
                })}
              </div>
              <div>
                <label style={lbl}>Business Description <span style={{ color: '#555', fontWeight: 400 }}>(optional)</span></label>
                <textarea name="businessDescription" rows="3" value={formData.businessDescription} onChange={handleChange} placeholder="Brief description of your products and business..." style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={handleBack} style={btnBack}>← Back</button>
                <button
                  type="submit"
                  disabled={formData.businessCategories.length === 0}
                  style={{ ...btnNext, opacity: formData.businessCategories.length === 0 ? 0.4 : 1, cursor: formData.businessCategories.length === 0 ? 'not-allowed' : 'pointer' }}
                >
                  Next: KYC & Documents →
                </button>
              </div>
            </form>
          )}

          {/* ─── STEP 3: KYC & Documents ─── */}
          {step === 3 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>KYC & Document Verification</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Required for legal compliance and verification by our team.</p>
              </div>

              <div style={{ padding: '0.85rem 1rem', background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '0.5rem' }}>
                <p style={{ color: '#E8B84B', fontSize: '0.75rem', margin: 0 }}>
                  📋 Enter your registration numbers and upload scanned copies or clear photos of your documents. Accepted: JPG, PNG, PDF.
                </p>
              </div>

              {/* GSTIN */}
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>GSTIN *</label>
                  <input name="gstin" value={formData.gstin} onChange={handleChange} required placeholder="e.g. 22AAAAA0000A1Z5" style={{ ...inp, textTransform: 'uppercase', fontFamily: 'monospace' }} />
                </div>
                <div style={col}>
                  <label style={lbl}>PAN Number *</label>
                  <input name="panNumber" value={formData.panNumber} onChange={handleChange} required placeholder="e.g. AAAAA0000A" style={{ ...inp, textTransform: 'uppercase', fontFamily: 'monospace' }} />
                </div>
              </div>

              {/* FSSAI — only if food category selected */}
              {isFoodVendor && (
                <div>
                  <label style={lbl}>FSSAI License Number * <span style={{ color: '#e87c5a', fontWeight: 400 }}>(Required for food/sevaOffering sellers)</span></label>
                  <input name="fssaiLicense" value={formData.fssaiLicense} onChange={handleChange} required={isFoodVendor} placeholder="e.g. 10012345000123" style={{ ...inp, fontFamily: 'monospace' }} />
                </div>
              )}

              {/* Document Uploads */}
              <DocUpload field="gstinCert"  label="GSTIN Certificate *"  hint="Upload your GST registration certificate" nested={true} />
              <DocUpload field="panCard"    label="PAN Card *"           hint="Upload a clear scan of your PAN card" nested={true} />
              {isFoodVendor && (
                <DocUpload field="fssaiCert" label="FSSAI Certificate *" hint="Upload your FSSAI license certificate" nested={true} />
              )}

              {/* Addresses */}
              <div>
                <label style={lbl}>Registered Business Address *</label>
                <textarea name="registeredAddress" rows="2" value={formData.registeredAddress} onChange={handleChange} required placeholder="As per GST/PAN registration" style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div>
                <label style={lbl}>Pickup / Warehouse Address <span style={{ color: '#555', fontWeight: 400 }}>(for order dispatch)</span></label>
                <textarea name="warehouseAddress" rows="2" value={formData.warehouseAddress} onChange={handleChange} placeholder="Leave blank if same as registered address" style={{ ...inp, resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={handleBack} style={btnBack}>← Back</button>
                <button type="submit" style={btnNext}>Next: Bank Details →</button>
              </div>
            </form>
          )}

          {/* ─── STEP 4: Bank Details ─── */}
          {step === 4 && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Bank Account Details</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>For automated weekly payouts after platform fee deduction.</p>
              </div>

              <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,153,51,0.05)', border: '1px solid rgba(255,153,51,0.15)', borderRadius: '0.5rem' }}>
                <p style={{ color: '#FF9933', fontSize: '0.75rem', margin: 0 }}>
                  🔒 Bank details are encrypted and only used for payout routing. Payouts are processed weekly after platform fee deduction.
                </p>
              </div>

              <div>
                <label style={lbl}>Bank Name *</label>
                <input name="bankName" value={formData.bank.bankName} onChange={handleBankChange} required placeholder="e.g. State Bank of India" style={inp} />
              </div>
              <div>
                <label style={lbl}>Account Holder Name (as on bank records) *</label>
                <input name="accountHolder" value={formData.bank.accountHolder} onChange={handleBankChange} required placeholder="e.g. Sharma Handicrafts Pvt Ltd" style={inp} />
              </div>
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>Account Number *</label>
                  <input name="accountNumber" value={formData.bank.accountNumber} onChange={handleBankChange} required placeholder="Account number" style={{ ...inp, fontFamily: 'monospace' }} />
                </div>
                <div style={col}>
                  <label style={lbl}>IFSC Code *</label>
                  <input name="ifscCode" value={formData.bank.ifscCode} onChange={handleBankChange} required placeholder="e.g. SBIN0001234" style={{ ...inp, textTransform: 'uppercase', fontFamily: 'monospace' }} />
                </div>
              </div>

              <DocUpload field="cancelledCheck" label="Cancelled Cheque *" hint="Upload a scanned copy of a cancelled cheque" nested={false} />

              <div style={{ padding: '0.85rem 1rem', background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                <p style={{ color: '#888', fontSize: '0.75rem', margin: 0 }}>
                  ✅ By submitting, you confirm that all information provided is accurate and legally valid. A Super Admin will review your application within 2–5 business days and send your login credentials to <strong style={{ color: '#E8B84B' }}>{formData.email}</strong>.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                <button type="button" onClick={handleBack} disabled={loading} style={btnBack}>← Back</button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...btnNext,
                    background: loading ? '#555' : 'linear-gradient(90deg, #E8B84B, #B8923A)',
                    color: loading ? '#999' : '#000',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? '⏳ Submitting…' : '🎉 Submit Vendor Application'}
                </button>
              </div>
            </form>
          )}

        </div>

        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <a
            href="/login"
            style={{ color: '#444', fontSize: '0.78rem', textDecoration: 'none' }}
            onMouseOver={e => e.target.style.color = '#E8B84B'}
            onMouseOut={e => e.target.style.color = '#444'}
          >
            ← Back to Login
          </a>
        </div>
        <p style={{ textAlign: 'center', marginTop: '0.75rem', color: '#8D8471', fontSize: '0.65rem' }}>Ananda Marga © 2026 — Vendor Onboarding Portal</p>
      </div>
    </div>
  );
};

export default VendorRegister;
